import React from "react";

import { trDts } from "@modules/datasheets/utils/i18n";

import { DatasheetComponentTextStyle } from "@common/dto/datasheet/component-settings/text-style";
import { DatasheetFieldComponentTextAlign } from "@common/dto/datasheet/field-component/text-align";
import { TableValue, useTableFieldController } from "./table";
import { HeadingValue, useHeadingController } from "./heading";
import { ContentValue, useContentController } from "./content";
import { AddedTextValue, useAddedTextController } from "./added-text";

export type DatasheetElementSettingsFieldComponentValue = {
  content: ContentValue;
  heading: HeadingValue;
  helpText: {
    textValue: string;
  };
  table?: TableValue;
};

export type DatasheetElementSettingsTextComponentValue = AddedTextValue;

export type DatasheetElementSettingsProps = {
  className?: string;
  title?: string;
  textComponentValue?: DatasheetElementSettingsTextComponentValue;
  onTextComponentChange: (value: DatasheetElementSettingsTextComponentValue) => void;
  fieldComponentValue?: DatasheetElementSettingsFieldComponentValue;
  onFieldComponentChange: (value: DatasheetElementSettingsFieldComponentValue) => void;
  defaultFieldComponentValue: DatasheetElementSettingsFieldComponentValue;
  onDefaultFieldComponentChange: (value: DatasheetElementSettingsFieldComponentValue) => void;
  defaultTextComponentValue?: DatasheetElementSettingsTextComponentValue;
  datasheetElement?: import("@modules/datasheets/types/datasheet-element").DatasheetElement<import("@common/dto/datasheet/field-component/get-dto").DatasheetFieldComponentGetDto>;
  formatId?: number;
  /** Current datasheet tab ID (for Configure Table so format is saved per tab). */
  currentTabId?: string | number;
  /**
   * Optional callback to open the "Add New Datasheet" modal preconfigured
   * to create a Table Datasheet for the currently selected table field.
   */
  onNewTableDatasheet?: (tableField: { id: number; name: string; context: number }) => void;
  /** Total count of tab-eligible fields on the current tab — used to clamp Tab Order input. */
  tabEligibleFieldCount?: number;
  /** Number of currently selected datasheet elements (used for multi-select behavior in UI). */
  selectedDatasheetElementsCount?: number;
};

export const useDatasheetElementSettingsController = (props: DatasheetElementSettingsProps) => {
  const { 
    textComponentValue,
    onTextComponentChange,
    fieldComponentValue, 
    onFieldComponentChange, 
    defaultFieldComponentValue,
    onDefaultFieldComponentChange,
    defaultTextComponentValue,
    datasheetElement,
    formatId,
    currentTabId,
  } = props;
  const [expanded, setExpanded] = React.useState(true);

  const handleFieldComponentChange = React.useCallback((value: Partial<DatasheetElementSettingsFieldComponentValue>) => {
    if (fieldComponentValue) {
      onFieldComponentChange({
        ...fieldComponentValue,
        ...value,
      });

      return;
    }

    onDefaultFieldComponentChange({
      ...defaultFieldComponentValue,
      ...value,
    });
  }, [fieldComponentValue, onFieldComponentChange, defaultFieldComponentValue, onDefaultFieldComponentChange]);

  const handleContentSectionChange = React.useCallback((value: ContentValue) => {
    handleFieldComponentChange({ content: value });
  }, [handleFieldComponentChange]);

  const handleHeadingSectionChange = React.useCallback((value: HeadingValue) => {
    handleFieldComponentChange({ heading: value });
  }, [handleFieldComponentChange]);

  const handleHelpTextChange = React.useCallback((textValue: string) => {
    handleFieldComponentChange({
      helpText: {
        textValue,
      },
    });
  }, [handleFieldComponentChange]);

  const handleTableChange = React.useCallback((value: TableValue) => {
    handleFieldComponentChange({ table: value });
  }, [handleFieldComponentChange]);

  const tableController = useTableFieldController({ 
    onChange: handleTableChange, 
    value: fieldComponentValue?.table,
    datasheetElement,
    formatId,
    currentTabId,
  });
  const contentController = useContentController({
    value: fieldComponentValue?.content || defaultFieldComponentValue.content,
    onChange: handleContentSectionChange,
  });
  const headingController = useHeadingController({
    value: fieldComponentValue?.heading || defaultFieldComponentValue.heading,
    onChange: handleHeadingSectionChange,
  });
  const addedTextController = useAddedTextController({
    value: textComponentValue || defaultTextComponentValue,
    onChange: onTextComponentChange,
  });

  const toggleExpanded = React.useCallback(() => {
    setExpanded((x) => !x);
  }, []);

  const textStyleOptions: Array<{ iconClassName: string; id: string; value: DatasheetComponentTextStyle }> = React.useMemo(
    () => [
      {
        iconClassName: "fa fa-bold",
        id: "btn-datasheet-design-right-panel-data-bold",
        value: DatasheetComponentTextStyle.bold,
      },
      {
        iconClassName: "fa fa-italic",
        id: "btn-datasheet-design-right-panel-data-italic",
        value: DatasheetComponentTextStyle.italic,
      },
      {
        iconClassName: "fa fa-underline",
        id: "btn-datasheet-design-right-panel-data-underline",
        value: DatasheetComponentTextStyle.underline,
      },
    ],
    []
  );

  const textAlignOptions: Array<{ iconClassName: string; id: string; value: DatasheetFieldComponentTextAlign }> = React.useMemo(
    () => [
      {
        iconClassName: "fa fa-align-left",
        id: "btn-datasheet-design-right-panel-data-align-left",
        value: DatasheetFieldComponentTextAlign.left,
      },
      {
        iconClassName: "fa fa-align-center",
        id: "btn-datasheet-design-right-panel-data-align-center",
        value: DatasheetFieldComponentTextAlign.center,
      },
      {
        iconClassName: "fa fa-align-right",
        id: "btn-datasheet-design-right-panel-data-align-right",
        value: DatasheetFieldComponentTextAlign.right,
      },
    ],
    []
  );

  const fontFamilyOptions = React.useMemo(
    () => [
      {
        value: "Arial",
        label: "Arial",
      },
      {
        value: "Comic Sans MS",
        label: "Comic Sans MS",
      },
      {
        value: "Courier New",
        label: "Courier New",
      },
      {
        value: "Georgia",
        label: "Georgia",
      },
      {
        value: "Lucida Sans Unicode",
        label: "Lucida Sans Unicode",
      },
      {
        value: "Tahoma",
        label: "Tahoma",
      },
      {
        value: "Times New Roman",
        label: "Times New Roman",
      },
      {
        value: "Trebuchet MS",
        label: "Trebuchet MS",
      },
      {
        value: "Verdana",
        label: "Verdana",
      },
    ],
    []
  );

  const fontSizesOptions = React.useMemo(
    () => [
      {
        label: "8",
        value: 8,
      },
      {
        label: "9",
        value: 9,
      },
      {
        label: "10",
        value: 10,
      },
      {
        label: "11",
        value: 11,
      },
      {
        label: "12",
        value: 12,
      },
      {
        label: "14",
        value: 14,
      },
      {
        label: "16",
        value: 16,
      },
      {
        label: "18",
        value: 18,
      },
      {
        label: "20",
        value: 20,
      },
      {
        label: "22",
        value: 22,
      },
      {
        label: "24",
        value: 24,
      },
      {
        label: "26",
        value: 26,
      },
      {
        label: "28",
        value: 28,
      },
      {
        label: "36",
        value: 36,
      },
      {
        label: "48",
        value: 48,
      },
      {
        label: "72",
        value: 72,
      },
    ],
    []
  );

  const headingAlignOptions = React.useMemo(
    () => [
      {
        value: DatasheetFieldComponentTextAlign.left,
        label: trDts("Left"),
      },
      {
        value: DatasheetFieldComponentTextAlign.center,
        label: trDts("Center"),
      },
      {
        value: DatasheetFieldComponentTextAlign.right,
        label: trDts("Right"),
      },
    ],
    []
  );

  const currentFieldComponentValue = fieldComponentValue || defaultFieldComponentValue;

  const currentTextComponentValue = textComponentValue || defaultTextComponentValue;

  return {
    addedTextController,
    currentFieldComponentValue,
    currentTextComponentValue,
    handleHelpTextChange,
    expanded,
    fontFamilyOptions,
    textStyleOptions,
    textAlignOptions,
    toggleExpanded,
    fontSizesOptions,
    headingAlignOptions,
    tableController,
    contentController,
    headingController,
  };
};
